import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anonymous, object = logged in
  const [loading, setLoading] = useState(true);

  const fetchMe = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // If session was invalidated by a newer login elsewhere, surface the toast + clear token
      const detail = e?.response?.data?.detail;
      const wasSignedOut = typeof detail === "string" && /signed in elsewhere|session ended/i.test(detail);
      if (e?.response?.status === 401 && wasSignedOut) {
        localStorage.removeItem("access_token");
        try { toast.error(detail, { duration: 5000 }); } catch (err) { /* ignore */ }
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    fetchMe();
  }, []);

  const login = async (email, password) => {
    // Clear any stale token BEFORE the login request — the api.js interceptor
    // attaches the token to every outbound request, including /auth/login.
    // A stale Bearer header on the login call doesn't break anything (login
    // doesn't check it), but clearing it now guarantees the post-login probe
    // uses ONLY the freshly-issued token from the response.
    localStorage.removeItem("access_token");
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.access_token) {
      localStorage.setItem("access_token", data.access_token);
    }
    // Fetch the enriched user (includes company_public_slug, company_name, plan, account_locked, etc.)
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      return me.data;
    } catch (e) {
      setUser(data.user);
      return data.user;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    localStorage.removeItem("access_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
